import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';

import CommonImage from '@/components/CommonImage';
import { useGetAllBrandsQuery, useGetAllUserQuery, useGetTagsQuery } from '@/api/commonApis';
import { useCreateArtworkMutation, useUpdateArtworkMutation } from '@/features/assets/api/assetsSlice';
import UploadArtwork from '@/features/assets/components/artwork/UploadArtwork';
import { toastError, toastSuccess } from '@/components/Toast';
import { ARTWORK_TAG_TYPES, INITIAL_STATE } from '@/features/assets/constants';
import Textarea from '@/components/Textarea';
import {
	createRequestBody,
	formatArtworkResponse,
	getChangedFields,
	validateArtwork
} from '@/features/assets/utils';

const AddArtworkModal = ({ artWorkResponse, isEdit, onCloseModal, onActionSucces }) => {
	const [artworkState, setArtworkState] = useState(INITIAL_STATE);
	const [originalState, setOriginalState] = useState(null);
	const [file, setFile] = useState(null);
	const [image, setImage] = useState(null);
	const [errors, setErrors] = useState({});
	const [isDisabledSubmit, setIsDisabledSubmit] = useState(false);

	const [updateArtwork] = useUpdateArtworkMutation();
	const [createArtwork] = useCreateArtworkMutation();

	const { data: brands } = useGetAllBrandsQuery({ params: { page: 0, size: 1000 } });
	const { data: allUser } = useGetAllUserQuery({
		params: { page: 0, size: 1000, userType: 'GRAPHICS_DESIGNER, FASHION_DESIGNER' }
	});
	const { data: seasonList } = useGetTagsQuery({
		params: { page: 0, size: 1000, type: ARTWORK_TAG_TYPES.ARTWORK_SEASON_TAG }
	});
	const { data: themeList } = useGetTagsQuery({
		params: { page: 0, size: 1000, type: ARTWORK_TAG_TYPES.ARTWORK_THEME_TAG }
	});
	const { data: allTags } = useGetTagsQuery({
		params: { page: 0, size: 1000, type: ARTWORK_TAG_TYPES.ARTWORK_TAGS }
	});

	useEffect(() => {
		if (isEdit) {
			const formattedArtworkResponse = formatArtworkResponse(artWorkResponse);
			setArtworkState(formattedArtworkResponse);
			setOriginalState(formattedArtworkResponse);
		}
	}, [artWorkResponse, isEdit]);

	const getImageUrl = (imgId) => {
		setArtworkState((prev) => ({
			...prev,
			imageId: imgId
		}));
	};

	const getFileId = (fileId) => {
		setArtworkState((prev) => ({
			...prev,
			fileId
		}));
	};

	const getUploadedFileName = (fileName) => {
		setArtworkState((prev) => ({
			...prev,
			fileName
		}));
	};

	const handleSubmit = () => {
		if (!validateArtwork(artworkState, setErrors)) {
			return;
		}

		if (isEdit) {
			const changedFields = getChangedFields(artworkState, originalState);

			if (Object.keys(changedFields).length === 0) {
				toastSuccess('No changes detected');
				onCloseModal();
				return;
			}

			updateArtwork({ body: changedFields, id: artWorkResponse?.id })
				.unwrap()
				.then(() => {
					toastSuccess('Artwork Updated');
					setArtworkState(INITIAL_STATE);
					setOriginalState(null);
					onActionSucces();
					onCloseModal();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
				});
		} else {
			const requestBody = createRequestBody(artworkState);
			createArtwork({ body: requestBody })
				.unwrap()
				.then(() => {
					toastSuccess('Artwork Created successfully');
					onActionSucces();
					setArtworkState(INITIAL_STATE);
					onCloseModal();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
				});
		}
	};

	return (
		<ModalContainer className="add-Sketch-modal" onCloseModal={() => {}}>
			<div className="common-popup add-Sketch-modal">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte d-flex align-items-center">
						<h3 className="semibold-16 mb-0">{isEdit === 'edit' ? 'Edit' : 'Add'} Artwork</h3>
					</div>
					<div className="close-btn" onClick={onCloseModal}>
						<CommonImage src="/icons/close.svg" alt="close" />
					</div>
				</div>
				<div className="common-popup-body scroll-y-label">
					<UploadArtwork
						onSuccessImageUpload={getImageUrl}
						onSuccessFileUpload={getFileId}
						artWorkResponse={artWorkResponse}
						file={file}
						setFile={setFile}
						image={image}
						setImage={setImage}
						errors={errors}
						setErrors={setErrors}
						onUploadedFileName={getUploadedFileName}
						setIsDisabledSubmit={setIsDisabledSubmit}
						artworkArtboardResponseList={artWorkResponse?.artworkArtboardResponseList}
					/>

					<div className="pom-form artwork-form">
						<div className="row">
							<div className="col-md-6 pr-2">
								<div className="form-group">
									<label htmlFor="collection">Season*</label>
									<CustomDropdown
										isShowLabel={false}
										toolTipLength={18}
										items={seasonList?.map((item) => ({
											...item,
											value: item?.text
										}))}
										selectedItem={artworkState?.season?.value}
										isAddNew={true}
										onItemClick={(item) => {
											setArtworkState({
												...artworkState,
												season: { ...item, text: item?.value }
											});
											setErrors((prev) => ({
												...prev,
												season: ''
											}));
										}}
									/>{' '}
									{errors?.season && <p className="error">{errors?.season}</p>}
								</div>
							</div>
							<div className="col-md-6 pl-2">
								<div className="form-group ">
									<label htmlFor="rise">Theme</label>
									<CustomDropdown
										isShowLabel={false}
										toolTipLength={18}
										items={themeList?.map((item) => ({
											...item,
											value: item?.text
										}))}
										selectedItem={artworkState?.theme?.value}
										isAddNewOnInline={true}
										onItemClick={(item) => {
											setArtworkState({
												...artworkState,
												theme: {
													id: item?.id,
													value: item?.value,
													text: item?.value
												}
											});
										}}
									/>{' '}
									{errors?.theme && <p className="error">{errors?.theme}</p>}
								</div>
							</div>
							<div className="col-md-6 pr-2">
								<div className="form-group">
									<label htmlFor="collection">Designed by*</label>
									<CustomDropdown
										isShowLabel={false}
										toolTipLength={18}
										items={allUser?.map((user) => ({
											...user,
											value: user?.name,
											label: user?.name
										}))}
										selectedItem={artworkState.designedBy?.name}
										isAddNewOnInline={false}
										onItemClick={(value) => {
											setArtworkState({ ...artworkState, designedBy: value });
											setErrors((prev) => ({
												...prev,
												designedBy: ''
											}));
										}}
									/>{' '}
									{errors?.designedBy && <p className="error">{errors?.designedBy}</p>}
								</div>
							</div>
							<div className="col-md-6 pl-2">
								<div className="form-group">
									<label htmlFor="collection">Brand</label>
									<CustomDropdown
										isShowLabel={false}
										toolTipLength={18}
										items={brands}
										selectedItem={artworkState?.brand?.name}
										isAddNewOnInline={false}
										onItemClick={(value) =>
											setArtworkState({ ...artworkState, brand: value })
										}
									/>{' '}
									{errors?.brand && <p className="error">{errors?.brand}</p>}
								</div>
							</div>

							<div className="col-md-6 pr-2">
								<div className="form-group no-shadow-react-select">
									<label htmlFor="inspired">Tag</label>
									<CreatableSelect
										isMulti
										value={artworkState.tag}
										onChange={(selected) =>
											setArtworkState((prev) => ({
												...prev,
												tag: selected?.map((item) => ({
													...item,
													text: item?.lab
												}))
											}))
										}
										options={allTags?.map((item) => ({
											...item,
											label: item?.text,
											value: item?.text
										}))}
										isClearable
										isValidNewOption={(inputValue) => inputValue.length > 0}
										onCreateOption={(inputValue) => {
											const newOption = {
												value: inputValue,
												label: inputValue,
												text: inputValue
											};
											setArtworkState((prev) => ({
												...prev,
												tag: [...prev.tag, newOption]
											}));
										}}
									/>
								</div>
							</div>

							<div className="col-md-6 pl-2">
								<div className="form-group">
									<label htmlFor="collection">Comments</label>
									<Textarea
										onChange={(e) =>
											setArtworkState((prev) => ({
												...prev,
												comments: e.target.value
											}))
										}
										value={artworkState.comments}
										placeholder="Comment"
										style={{
											height: '36px',
											padding: '6px 12px'
										}}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer pt-0">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32 ml-2" onClick={onCloseModal}>
							Cancel
						</button>
						<button
							className="button size32 ml-2"
							onClick={handleSubmit}
							disabled={isDisabledSubmit}
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddArtworkModal;
